import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from './Icon';
import { supabase } from '../supabase';
import BiohackInfoCard from './BiohackInfoCard';
import ProtocolShield from './ProtocolShield';
import JellyfishLoader from './JellyfishLoader';
import './TrendingSection.css';
import { useProtocolActions } from '../hooks/useProtocolActions';
import { useUserProtocolStatus } from '../contexts/UserProtocolStatusContext';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';

const ORDINAL_WORDS = [
  '',
  'First',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eighth',
  'Ninth',
  'Tenth',
];

const getOrdinalText = (rank) => {
  if (typeof rank !== 'number' || Number.isNaN(rank)) {
    return '';
  }

  if (rank > 0 && rank < ORDINAL_WORDS.length) {
    return ORDINAL_WORDS[rank];
  }

  if (rank <= 0) {
    return '';
  }

  const remainder10 = rank % 10;
  const remainder100 = rank % 100;

  if (remainder10 === 1 && remainder100 !== 11) {
    return `${rank}st`;
  }

  if (remainder10 === 2 && remainder100 !== 12) {
    return `${rank}nd`;
  }

  if (remainder10 === 3 && remainder100 !== 13) {
    return `${rank}rd`;
  }

  return `${rank}th`;
};

const getOrdinalNumber = (rank) => {
  if (typeof rank !== 'number' || Number.isNaN(rank) || rank <= 0) {
    return '';
  }

  const remainder10 = rank % 10;
  const remainder100 = rank % 100;

  if (remainder10 === 1 && remainder100 !== 11) {
    return `${rank}st`;
  }

  if (remainder10 === 2 && remainder100 !== 12) {
    return `${rank}nd`;
  }

  if (remainder10 === 3 && remainder100 !== 13) {
    return `${rank}rd`;
  }

  return `${rank}th`;
};

const getOrdinalParts = (rank) => {
  if (typeof rank !== 'number' || Number.isNaN(rank) || rank <= 0) {
    return { number: '', suffix: '' };
  }

  const remainder10 = rank % 10;
  const remainder100 = rank % 100;

  let suffix = 'th';
  if (remainder10 === 1 && remainder100 !== 11) {
    suffix = 'st';
  } else if (remainder10 === 2 && remainder100 !== 12) {
    suffix = 'nd';
  } else if (remainder10 === 3 && remainder100 !== 13) {
    suffix = 'rd';
  }

  return { number: rank.toString(), suffix };
};

const sortProtocolsByRank = (protocols) => {
  return (protocols || [])
    .slice()
    .sort((a, b) => {
      // Handle null, undefined, or non-numeric ranks by treating them as having no rank (goes to end)
      const rankA = (a.activeness_rank != null && Number.isFinite(a.activeness_rank)) 
        ? a.activeness_rank 
        : Number.MAX_SAFE_INTEGER;
      const rankB = (b.activeness_rank != null && Number.isFinite(b.activeness_rank)) 
        ? b.activeness_rank 
        : Number.MAX_SAFE_INTEGER;

      // Sort in ascending order: rank 1 first, rank 2 second, etc.
      if (rankA !== rankB) {
        return rankA - rankB;
      }

      // If ranks are equal, maintain original order (stable sort)
      return 0;
    });
};

const TrendingSection = () => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [trendingProtocols, setTrendingProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedCards, setCollapsedCards] = useState({});
  const [selectedBiohack, setSelectedBiohack] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const [ratedBiohackIds, setRatedBiohackIds] = useState(new Set());

  // Resolve current user for favourites and ratings
  useEffect(() => {
    const resolveUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUserId(session?.user?.id ?? null);
    };
    resolveUser();
  }, []);

  // Fetch current user's favourites for BiohackInfoCard
  useEffect(() => {
    const fetchFavourites = async () => {
      if (!currentUserId) return;
      try {
        const cacheKey = getCacheKey('fetchFavourites', currentUserId);
        const data = await dedupeRequest(cacheKey, async () => {
          const { data: favData, error } = await supabase
            .from('hack_favourited')
            .select('hack_id')
            .eq('user_id', currentUserId);
          if (error) throw error;
          return favData || [];
        });
        setFavourites(data);
      } catch (e) {
        console.error('Error fetching favourites in TrendingSection:', e);
      }
    };
    fetchFavourites();
  }, [currentUserId]);

  // Fetch current user's rated biohack IDs for BiohackInfoCard star state
  useEffect(() => {
    const fetchUserRatings = async () => {
      if (!currentUserId) return;
      try {
        const cacheKey = getCacheKey('fetchUserRatings', currentUserId);
        const data = await dedupeRequest(cacheKey, async () => {
          const { data: ratingData, error } = await supabase
            .from('hack_scoring')
            .select('hack_id')
            .eq('user_id', currentUserId);
          if (error) throw error;
          return ratingData || [];
        });
        setRatedBiohackIds(new Set((data || []).map(r => r.hack_id)));
      } catch (e) {
        console.error('Error fetching user ratings in TrendingSection:', e);
      }
    };
    fetchUserRatings();
  }, [currentUserId]);

  const handleToggleFavourite = useCallback(async (hackId) => {
    if (!currentUserId) return;
    try {
      const isFavourited = favourites.some(fav => fav.hack_id === hackId);
      if (isFavourited) {
        const { error } = await supabase
          .from('hack_favourited')
          .delete()
          .eq('user_id', currentUserId)
          .eq('hack_id', hackId);
        if (error) throw error;
        setFavourites(prev => prev.filter(fav => fav.hack_id !== hackId));
      } else {
        const { error } = await supabase
          .from('hack_favourited')
          .insert({
            user_id: currentUserId,
            hack_id: hackId,
            timestamp: new Date().toISOString()
          });
        if (error) throw error;
        setFavourites(prev => [...prev, { hack_id: hackId }]);
      }
    } catch (e) {
      console.error('Error toggling favourite:', e);
    }
  }, [currentUserId, favourites]);

  // Use centralized protocol actions hook
  const {
    userVotes,
    votingProtocolId,
    likedProtocols,
    likingProtocolId,
    handleVote: handleVoteAction,
    handleToggleLike: handleToggleLikeAction,
    fetchUserVotes,
    fetchLikedProtocols
  } = useProtocolActions();

  const sortedTrendingProtocols = useMemo(
    () => sortProtocolsByRank(trendingProtocols),
    [trendingProtocols]
  );

  // Toggle individual card collapse state (only one card can be expanded at once)
  const toggleCard = useCallback((protocolId) => {
    setCollapsedCards(prev => {
      const isCurrentlyExpanded = prev[protocolId] === false;
      
      // If clicking on an expanded card, collapse it
      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [protocolId]: true // Collapse the clicked card
        };
      }
      
      // If clicking on a collapsed card, expand it and collapse all others
      const newState = {};
      sortedTrendingProtocols.forEach(protocol => {
        if (protocol.id === protocolId) {
          newState[protocol.id] = false; // Expand the clicked card
        } else {
          newState[protocol.id] = true; // Collapse all other cards
        }
      });
      
      return newState;
    });
  }, [sortedTrendingProtocols]);

  // Check if a card is collapsed (default to true for new cards)
  const isCardCollapsed = (protocolId) => {
    return collapsedCards[protocolId] !== false; // Default to collapsed if not set
  };

  // Check if all cards are collapsed
  const areAllCardsCollapsed = () => {
    return sortedTrendingProtocols.every(protocol => isCardCollapsed(protocol.id));
  };

  // Toggle all cards
  const toggleAllCards = () => {
    const allCollapsed = areAllCardsCollapsed();
    const newState = {};
    sortedTrendingProtocols.forEach(protocol => {
      newState[protocol.id] = allCollapsed; // If all collapsed, expand all; if not, collapse all
    });
    setCollapsedCards(newState);
  };

  // Fetch user's liked protocols and votes on mount
  // Note: fetchUserVotes now fetches both votes and liked protocols in a single query
  useEffect(() => {
    fetchUserVotes();
  }, [fetchUserVotes]);

  const fetchTrendingProtocols = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use deduplication to prevent duplicate requests (especially from React Strict Mode)
      const cacheKey = getCacheKey('fetchTrendingProtocols', 'home');
      const protocols = await dedupeRequest(cacheKey, async () => {
        // RLS on protocols allows viewing when: tab privacy allows it, OR protocol is
        // attached to an accessible post (e.g. public post) — so trending shows protocols
        // the user can see either way, including those shared via public posts
        const { data, error } = await supabase
          .from('protocols')
          .select(`
            *,
            adherence_score,
            pop_score,
            protocol_hacks ( 
              hack_id, 
              position,
              biohacks (
                id,
                name,
                image_url,
                category
              )
            ),
            protocols_status ( user_id, is_active ),
            protocol_ratings ( id, rating ),
            user_profiles!protocols_created_by_fkey (
              user_id,
              username
            )
          `)
          .order('trending_rank', { ascending: true })
          .limit(5);

        if (error) throw error;
        return data;
      });
      
      if (!protocols || protocols.length === 0) {
        setTrendingProtocols([]);
        setLoading(false);
        return;
      }
      
      // Fetch total active user counts for all protocols using RPC function
      // This bypasses RLS to get the true count used for ranking
      const protocolIds = protocols.map(p => p.id);
      const activeCountsMap = new Map();
      
      // Call the database function for each protocol to get total active count
      await Promise.all(
        protocolIds.map(async (protocolId) => {
          const { data, error } = await supabase.rpc('get_protocol_active_count', {
            protocol_id_param: protocolId
          });
          if (!error && data !== null) {
            activeCountsMap.set(protocolId, data);
          }
        })
      );
      
      const protocolsWithHacks = protocols.map((protocol, index) => {
        // Assign rank 1-5 based on position (not global trending_rank)
        // RLS already filtered to top 5 visible protocols by trending_rank
        const activenessRank = index + 1; // 1-based index (1, 2, 3, 4, 5)
        
        const protocolRatings = protocol.protocol_ratings || [];
        const likesEntries = protocolRatings.filter(rating => typeof rating.rating === 'number' && rating.rating >= 4);
        const upvoteEntries = protocolRatings.filter(rating => rating.rating === 1);
        const downvoteEntries = protocolRatings.filter(rating => rating.rating === -1);

        // Map protocol_hacks to actual biohack objects (biohacks are already included in query)
        const hacks = (protocol.protocol_hacks || [])
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map(h => h.biohacks)
          .filter(Boolean);
        
        // Get total active count from the database function (bypasses RLS, matches ranking)
        const activeCount = activeCountsMap.get(protocol.id) || 0;
        
        // Count total users who have added this protocol (from nested query, also RLS filtered)
        const usersCount = protocol.protocols_status ? protocol.protocols_status.length : 0;
        
        // Count likes for this protocol (rating >= 4 treated as like)
        const likesCount = likesEntries.length;
        
        // Get creator username from joined user_profiles data
        const creatorUsername = protocol.user_profiles?.username || 'Unknown Creator';
        
        return { 
          ...protocol, 
          hacks,
          created_by_username: creatorUsername,
          active_count: activeCount,
          users_count: usersCount,
          usage_count: usersCount,
          likes_count: likesCount,
          upvotes_count: upvoteEntries.length,
          downvotes_count: downvoteEntries.length,
          activeness_rank: activenessRank // Use the trending_rank from database
        };
      });
      
      // Database already orders by trending_rank, so we only need to sort if order might have changed
      // However, we still sort to handle any edge cases with null/undefined ranks
      setTrendingProtocols(sortProtocolsByRank(protocolsWithHacks));
    } catch (error) {
      console.error('Error fetching trending protocols:', error);
      setTrendingProtocols([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrendingProtocols();
  }, [fetchTrendingProtocols]);

  // Expand the first card (rank 1) by default when protocols are loaded
  useEffect(() => {
    if (sortedTrendingProtocols.length > 0 && Object.keys(collapsedCards).length === 0) {
      const firstProtocol = sortedTrendingProtocols.find(p => p.activeness_rank === 1) || sortedTrendingProtocols[0];
      if (firstProtocol) {
        const initialState = {};
        sortedTrendingProtocols.forEach(protocol => {
          initialState[protocol.id] = protocol.id === firstProtocol.id ? false : true;
        });
        setCollapsedCards(initialState);
      }
    }
  }, [sortedTrendingProtocols]);

  // Handle protocol like toggle (wrapper for hook)
  const handleToggleLike = async (protocol) => {
    await handleToggleLikeAction(
      protocol,
      (result) => {
        // Update trending protocols state on success
        setTrendingProtocols(prev => {
          const updated = prev.map(p => 
            p.id === result.protocol_id 
              ? { ...p, likes_count: result.likes_count }
              : p
          );
          return sortProtocolsByRank(updated);
        });

        // Note: Re-fetch is expensive - only do it if rankings might have changed
        // For now, we skip re-fetch since likes_count doesn't directly affect trending_rank
      },
      (errorMsg) => {
        alert(errorMsg);
      }
    );
  };

  // Handle upvote/downvote protocol (wrapper for hook)
  const handleVote = async (protocol, voteType) => {
    // Optimistically update UI for immediate feedback
    setTrendingProtocols(prev => prev.map(p => {
      if (p.id === protocol.id) {
        const currentVote = userVotes[protocol.id];
        let newUpvotes = p.upvotes_count || 0;
        let newDownvotes = p.downvotes_count || 0;
        
        if (currentVote === 1) newUpvotes -= 1;
        else if (currentVote === -1) newDownvotes -= 1;
        
        const newVote = currentVote === voteType ? null : voteType;
        if (newVote === 1) newUpvotes += 1;
        else if (newVote === -1) newDownvotes += 1;
        
        return { ...p, upvotes_count: newUpvotes, downvotes_count: newDownvotes };
      }
      return p;
    }));
    
    await handleVoteAction(
      protocol,
      voteType,
      (result) => {
        // Update trending protocols state on success
        setTrendingProtocols(prev => {
          const updated = prev.map(p => 
            p.id === result.protocol_id 
              ? { ...p, upvotes_count: result.upvotes_count, downvotes_count: result.downvotes_count }
              : p
          );
          return sortProtocolsByRank(updated);
        });

        // Note: Re-fetch is expensive - rankings update periodically on server
        // Optimistically updating counts is sufficient for good UX
      },
      (errorMsg) => {
        alert(errorMsg);
      }
    );
  };

  // Handle like protocol (rating = 1) - call handleVoteAction directly like AttachmentDisplay
  const handleLike = async (protocol) => {
    await handleVoteAction(
      protocol,
      1,
      async (result) => {
        // Update trending protocols state on success
        setTrendingProtocols(prev => {
          const updated = prev.map(p => 
            p.id === result.protocol_id 
              ? { ...p, upvotes_count: result.upvotes_count, downvotes_count: result.downvotes_count }
              : p
          );
          return sortProtocolsByRank(updated);
        });
        // Refresh userVotes to update the UI
        await fetchUserVotes();
      },
      (errorMsg) => {
        console.error('Error liking protocol:', errorMsg);
      }
    );
  };

  // Handle dislike protocol (rating = -1) - call handleVoteAction directly like AttachmentDisplay
  const handleDislike = async (protocol) => {
    await handleVoteAction(
      protocol,
      -1,
      async (result) => {
        // Update trending protocols state on success
        setTrendingProtocols(prev => {
          const updated = prev.map(p => 
            p.id === result.protocol_id 
              ? { ...p, upvotes_count: result.upvotes_count, downvotes_count: result.downvotes_count }
              : p
          );
          return sortProtocolsByRank(updated);
        });
        // Refresh userVotes to update the UI
        await fetchUserVotes();
      },
      (errorMsg) => {
        console.error('Error disliking protocol:', errorMsg);
      }
    );
  };

  const { hasProtocol, refresh: refreshProtocolStatuses } = useUserProtocolStatus();

  // Handle adding protocol to staged protocols
  const handleAddToStaged = useCallback(async (protocol) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        console.log('Please sign in to add protocols');
        return false;
      }

      if (!protocol || !protocol.id) {
        console.error('Invalid protocol');
        return false;
      }

      if (hasProtocol(protocol.id)) {
        return true;
      }

      const protocolHacks = protocol.biohacks || protocol.hacks || protocol.protocol_hacks || [];
      if (protocolHacks.length === 0) {
        console.log('Cannot add a protocol with no biohacks');
        return false;
      }

      const { error: statusError } = await supabase
        .from('protocols_status')
        .insert({
          user_id: user.id,
          protocol_id: protocol.id,
          is_active: false,
          added_at: new Date().toISOString()
        });

      if (statusError) {
        if (statusError.code === '23505') {
          await refreshProtocolStatuses();
          return true;
        }
        console.error('Error adding protocol to staged:', statusError);
        return false;
      }

      await refreshProtocolStatuses();
      console.log('Protocol added to your staged protocols!');
      return true;
    } catch (error) {
      console.error('Error adding protocol to staged:', error);
      return false;
    }
  }, [hasProtocol, refreshProtocolStatuses]);

  return (
    <nav className="trending-section">
      {/* Header Section */}
      <div className="trending-header">
        <div className="header-content">
          <div className="header-title-row">
            <span className="header-title-text">Trending Protocols</span>
          </div>
        </div>
      </div>

      {/* Trending Content */}
      <div className="trending-content">
        {loading ? (
          <div className="loading-message">
            <JellyfishLoader />
          </div>
        ) : trendingProtocols.length === 0 ? (
          <div className="empty-message">No protocols available yet</div>
        ) : (
          <div className="trending-list">
            {sortedTrendingProtocols.map((protocol, index) => {
              const isLiked = likedProtocols.includes(protocol.id);
              const isLiking = likingProtocolId === protocol.id;
              const cardCollapsed = isCardCollapsed(protocol.id);
              const originalName = protocol.name || '';
              const rankBannerText = getOrdinalText(protocol.activeness_rank);
              
              return (
                <div
                  key={`trending-protocol-${protocol.id}-${index}`}
                  className={`trending-item ${hoveredItem === protocol.id ? 'hovered' : ''} ${cardCollapsed ? 'collapsed' : 'expanded'}`}
                  onMouseEnter={() => setHoveredItem(protocol.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  {cardCollapsed ? (
                    <div className="trending-item-collapsed" onClick={() => toggleCard(protocol.id)}>
                      <div className="trending-left">
                        <div className={`trending-rank rank-${protocol.activeness_rank}`}>
                          {(() => {
                            const { number, suffix } = getOrdinalParts(protocol.activeness_rank);
                            return number ? <>{number}<sup>{suffix}</sup></> : '';
                          })()}
                        </div>
                        <div className="trending-name-tag" title={originalName}>
                          <span className="trending-name-tag__text">
                            {originalName}
                          </span>
                        </div>
                      </div>
                      <div className="trending-right">
                        <div className="trending-stats-inline">
                          <div className="inline-stat">
                            <Icon name="Users" size={16} color="var(--color-success-dark)" />
                            <span className="inline-stat-value">{protocol.active_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="trending-expanded-card-outer">
                      <div className="trending-expanded-card-container">
                        <div
                          className={`trending-card-rank-badge rank-${protocol.activeness_rank}`}
                          aria-label={`Rank ${protocol.activeness_rank}`}
                        >
                          {(() => {
                            const { number, suffix } = getOrdinalParts(protocol.activeness_rank);
                            return number ? <>{number}<sup>{suffix}</sup></> : '';
                          })()}
                        </div>
                        <button
                          className="trending-card-close-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCard(protocol.id);
                          }}
                          aria-label="Close card"
                        >
                          <Icon name="X" className="icon--sm" />
                        </button>
                        <ProtocolShield 
                          protocol={protocol} 
                          size="small" 
                          onBiohackClick={setSelectedBiohack}
                          onLike={handleLike}
                          onDislike={handleDislike}
                          userVote={userVotes[protocol.id] || null}
                          onAddToStaged={handleAddToStaged}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Biohack Info Card Modal */}
      {selectedBiohack && (
        <BiohackInfoCard
          biohack={selectedBiohack}
          onClose={() => setSelectedBiohack(null)}
          isFavourited={favourites.some(fav => fav.hack_id === selectedBiohack.id)}
          onToggleFavourite={() => handleToggleFavourite(selectedBiohack.id)}
          hasRated={ratedBiohackIds.has(selectedBiohack.id)}
          onRatingSubmitted={() => {
            setRatedBiohackIds(prev => new Set([...prev, selectedBiohack.id]));
          }}
        />
      )}
    </nav>
  );
};

export default TrendingSection;
